import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Logo from "@/components/7fcc/Logo";
import { HERO } from "@/data/content";
import { EASE } from "@/lib/motion";

// Ultra-compact hero: chrome brand plate, eyebrow and headline only.
export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const logoX = useTransform(sx, [-0.5, 0.5], [-8, 8]);
  const logoY = useTransform(sy, [-0.5, 0.5], [-5, 5]);

  const onMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex flex-col items-center overflow-hidden bg-ink-950 px-6 pb-14 pt-12 text-center md:pb-16 md:pt-14"
      data-testid="hero-section"
    >
      {/* soft ambient glow behind the logo */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-[2%] h-[42vh] w-[70vw] max-w-[880px] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(61,126,255,0.1),transparent_65%)] blur-3xl"
      />

      <motion.div
        style={{ x: logoX, y: logoY }}
        initial={{ opacity: 0, scale: 0.96, filter: "blur(10px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.1, ease: EASE }}
        className="relative w-[min(420px,70vw)] mix-blend-screen"
      >
        <Logo halo priority className="w-full" />
      </motion.div>

      <div className="relative mt-5 flex max-w-5xl flex-col items-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-light"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
          {HERO.eyebrow}
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
        </motion.p>

        <h1 className="font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-semibold uppercase leading-[1.1] tracking-[-0.01em]">
          {HERO.headlineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="text-chrome block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45 + i * 0.13, ease: EASE }}
                data-testid={`hero-headline-${i + 1}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
}
